import React, { useEffect, useState } from "react";
import { api } from "../api";
import { useAuth } from "../context/AuthContext";
import Avatar from "../components/Avatar";

function formatSv(date) {
  if (!date) return "-";
  const d = new Date(date);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const HH = String(d.getHours()).padStart(2, "0");
  const MM = String(d.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}-${yyyy} ${HH}:${MM}`;
}

export default function CompletedAdmin() {
  const { fetchAssignableUsers } = useAuth();

  const [todos, setTodos] = useState([]);
  const [users, setUsers] = useState([]);

  // Filter state
  const [text, setText] = useState("");
  const [createdFrom, setCreatedFrom] = useState("");
  const [createdTo, setCreatedTo] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [assignedTo, setAssignedTo] = useState("");

  // Pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);

  const [loading, setLoading] = useState(false);

  // Modal
  const [selected, setSelected] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/todos/completed-admin", {
        params: {
          text,
          assignedTo,
          from,
          to,
          createdFrom,
          createdTo,
          page,
          limit,
        },
      });
      setTodos(data.todos);
      setPages(Number(data.pages));
      setTotal(data.total);
    } catch (e) {
      console.error("❌ Fetch error", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      const list = await fetchAssignableUsers();
      setUsers(list);
    })();
  }, [fetchAssignableUsers]);

  useEffect(() => {
    fetchData();
    window.scrollTo(0, 0); // alltid till toppen vid sidbyte
  }, [page, limit]);

  const clearFilters = () => {
    setText("");
    setCreatedFrom("");
    setCreatedTo("");
    setFrom("");
    setTo("");
    setAssignedTo("");
    setPage(1);
    fetchData();
  };

  const handleUndo = async (todo) => {
    const todoId = todo.id ?? todo._id;
    if (!window.confirm("Ångra slutförd status på detta jobb?")) return;
    await api.post(`/todos/${todoId}/complete`);
    fetchData();
    setSelected(null);
  };

  const handleDelete = async (todo) => {
    const todoId = todo.id ?? todo._id;
    if (!window.confirm("Ta bort detta jobb permanent?")) return;
    await api.delete(`/todos/${todoId}`);
    fetchData();
    setSelected(null);
  };
  
  return (
    <div className="container mt-5">
      <h3 className="mb-4">Slutförda Jobb</h3>

      {/* Filter */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-2">
            <div className="col-md-3">
              <label className="form-label small">Sök text</label>
              <input
                className="form-control"
                placeholder="Sök text…"
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label small">Skapad från</label>
              <input
                type="date"
                className="form-control"
                value={createdFrom}
                onChange={(e) => setCreatedFrom(e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label small">Skapad till</label>
              <input
                type="date"
                className="form-control"
                value={createdTo}
                onChange={(e) => setCreatedTo(e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label small">Slutförd från</label>
              <input
                type="date"
                className="form-control"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
            </div>
            <div className="col-md-2">
              <label className="form-label small">Slutförd till</label>
              <input
                type="date"
                className="form-control"
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label small">Användare</label>
              <select
                className="form-select"
                value={assignedTo}
                onChange={(e) => setAssignedTo(e.target.value)}
              >
                <option value="">Alla</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.firstName} {u.lastName}{" "}
                    {u.phone ? `(${u.phone})` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2 d-flex align-items-end">
              <select
                className="form-select"
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(1);
                }}
              >
                <option value={25}>25 per sida</option>
                <option value={50}>50 per sida</option>
                <option value={100}>100 per sida</option>
              </select>
            </div>
            <div className="col-md-2 d-flex align-items-end">
              <button
                className="btn btn-primary w-100"
                onClick={() => {
                  setPage(1);
                  fetchData();
                }}
              >
                Sök
              </button>
            </div>
            <div className="col-md-2 d-flex align-items-end">
              <button className="btn btn-outline-secondary w-100" onClick={clearFilters}>
                Rensa
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Lista */}
      <div className="card">
        <div className="card-body">
          {loading ? (
            <div>Laddar…</div>
          ) : todos.length === 0 ? (
            <div className="text-muted">Inga resultat</div>
          ) : (
            <div className="row g-3">
              {loading ? (
                <div className="text-muted">Laddar…</div>
              ) : todos.length === 0 ? (
                <p className="text-muted">Inga resultat</p>
              ) : (
                todos.map((todo) => (
                  <div
                    key={todo._id}
                    className="col-12"
                    style={{ cursor: "pointer" }}
                    onClick={() => setSelected(todo)}
                  >
                    <div className="card shadow-sm h-100 position-relative">

                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center">
                          <h5 className="card-title ">
                            {todo.text}
                          </h5>
                          
                        </div>

                        <p
                          className="card-text small text-muted d-flex align-items-center gap-2"
                          style={{ minHeight: "24px" }}
                        >
                          {todo.assignedTo && (
                            <span className="d-flex align-items-center gap-1">
                              Tilldelad: 
                              <span className="badge bg-light text-dark">
                                <Avatar
                                  initials={todo.assignedTo.avatar?.initials}
                                  color={todo.assignedTo.avatar?.color}
                                  size={18}
                                />{" "}
                                {todo.assignedTo.firstName}
                              </span>
                            </span>
                          )}

                        </p>

                        {todo.phone && (
                          <p className="card-text small text-muted mb-0">Tel: {todo.phone}</p>
                        )}


                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>

          )}

          {/* Pagination */}
          <div className="d-flex justify-content-between align-items-center mt-3">
            <div>
              Sida {page} av {pages} (Totalt {total})
            </div>
            <div className="d-flex gap-2">
              <button
                className="btn btn-outline-secondary btn-sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Föregående
              </button>
              <input
                type="number"
                min={1}
                max={pages}
                value={page}
                onChange={(e) => setPage(Number(e.target.value))}
                className="form-control form-control-sm"
                style={{ width: 80 }}
              />
              <button
                className="btn btn-outline-secondary btn-sm"
                disabled={page >= pages}
                onClick={() => setPage((p) => Math.min(Number(pages), p + 1))}
              >
                Nästa
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {selected && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{ background: "rgba(0,0,0,.5)" }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Jobbdetaljer</h5>
                <button type="button" className="btn-close" onClick={() => setSelected(null)} />
              </div>
              <div className="modal-body">
                <p><b>Text:</b> {selected.text}</p>
                <p><b>Telefon:</b> {selected.phone || "–"}</p>
                <p><b>Skapad:</b> {formatSv(selected.createdAt)} av {selected.createdBy?.firstName} {selected.createdBy?.lastName}</p>
                <p><b>Slutförd:</b> {formatSv(selected.completedAt)} av {selected.completedBy?.firstName} {selected.completedBy?.lastName}</p>
                <p><b>Tilldelad:</b> {selected.assignedTo ? `${selected.assignedTo.firstName} ${selected.assignedTo.lastName}` : "–"}</p>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => handleUndo(selected)}>Ångra slutförd</button>
                <button className="btn btn-danger" onClick={() => handleDelete(selected)}>Ta bort</button>
                <button className="btn btn-outline-secondary" onClick={() => setSelected(null)}>Stäng</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
